import { Routes } from '@angular/router';
import { LoginComponent } from './pages/auth/components/login/login.component';
import { DashboardComponent } from './pages/dashboard/components/dashboard/dashboard.component';
import { authGuard } from './shared/guards/auth/auth.guard';
import { anonymousGuard } from './shared/guards/anonymous/anonymous.guard';
import { siteTypeGuard } from './shared/guards/site-type/site-type.guard';
import { ErrorComponent } from './shared/components/error/error.component';
import { LogInCallbackComponent } from './pages/auth/components/login-callback/login-callback.component';
import { AssignedLeadsComponent } from './pages/leads/components/assigned-leads/assigned-leads.component';
import { PageLayout } from './shared/enums/page-layout';
import { setLayoutResolver } from './shared/resolvers/set-layout.resolver';
import { DocumentsComponent } from './pages/documents/components/documents/documents.component';
import { ManageConnectionGroupComponent } from './pages/manage-connection-group/components/manage-connection-group/manage-connection-group.component';
import { ManageUsersComponent } from './pages/manage-users/components/manage-users/manage-users.component';
import { ManageDuplicatesComponent } from './pages/manage-duplicates/components/manage-duplicates/manage-duplicates.component';
import { LicenseAgreementComponent } from './pages/license-agreement/components/license-agreement/license-agreement.component';
import { NotificationsComponent } from './pages/notifications/components/notifications/notifications.component';
import { CdfiOutreachLogsComponent } from './pages/logs/CDFI-outreach/components/cdfi-outreach-logs/cdfi-outreach-logs.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/components/privacy-policy/privacy-policy.component';
import { TermsOfUseComponent } from './pages/terms-of-use/terms-of-use.component';
import { CdfiLeadsQuotaComponent } from './pages/configs/CDFI-leads-quota/components/cdfi-leads-quota/cdfi-leads-quota.component';
import { OutreachComponent } from './pages/outreach/components/outreach/outreach.component';
import { OutreachLinksComponent } from './pages/outreach/components/outreach-links/outreach-links.component';
import { OutreachLinkCallbackComponent } from './pages/outreach/components/outreach-link-callback/outreach-link-callback.component';
import { LeadsCriteriaComponent } from './pages/outreach/components/leads-criteria/leads-criteria.component';
import { LeadsCriteriaDetailComponent } from './pages/outreach/components/leads-criteria-detail/leads-criteria-detail.component';
import { UserSelfSignupComponent } from './pages/self-signup/components/user-self-signup/user-self-signup.component';
import { ConnectionSelfSignupComponent } from './pages/self-signup/components/connection-self-signup/connection-self-signup.component';
import { ConnectionSignupRequestsComponent } from './pages/self-signup/components/connection-signup-requests/connection-signup-requests.component';
import { OrgOnboardingCompleteComponent } from './pages/self-signup/components/org-onboarding-complete/org-onboarding-complete.component';
import { UserProfileComponent } from './pages/my-profile/components/user-profile/user-profile.component';
import { LeadStatusLogsComponent } from './pages/logs/lead-status/components/lead-status-logs/lead-status-logs.component';
import { DeeprootsLogsComponent } from './pages/logs/deeproots/components/deeproots-logs/deeproots-logs.component';
import { AuditLogsComponent } from './pages/logs/audit-logs/components/audit-logs/audit-logs.component';
import { EmailLogComponent } from './pages/logs/email-log/components/email-log/email-log.component';
import { GmailSignupErrorComponent } from './pages/auth/components/gmail-signup-error/gmail-signup-error.component';
import { DndListComponent } from './pages/configs/dnd-list/components/dnd-list.component';
import { AnnouncementComponent } from './pages/announcements/components/announcement/announcement.component';
import { AiSearchLabComponent } from './pages/search/components/ai-search-lab/ai-search-lab.component';
import { FindLeadsV2Component } from './pages/leads/components/find-leads-v2/find-leads-v2.component';
import { CdfiListComponent } from './pages/sme/components/cdfi-list/cdfi-list.component';
import { UserProfileDetailsComponent } from './pages/my-profile/components/user-profile-details/user-profile-details.component';
import { CdfiProfileDetailsComponent } from './pages/my-profile/components/cdfi-profile-details/cdfi-profile-details.component';
import { ChangePasswordComponent } from './pages/auth/components/change-password/change-password.component';
import { reauthGuard } from './shared/guards/re-auth/re-auth';
import { BusinessProfilesComponent } from './pages/sme/components/business-profiles/business-profiles.component';
import { ProductDetailPageComponent } from './pages/sme/components/product-detail-page/product-detail-page.component';
import { MatchesComponent } from './pages/sme/components/matches/matches.component';
import { UserAccountType } from './shared/models/user';
import { AchievementsComponent } from './pages/achievements/components/achievements/achievements.component';
import { UserFlagsComponent } from './pages/reports/components/user-flags/user-flags.component';
import { FlagEmailComponent } from './pages/reports/components/flag-email/flag-email.component';
import { SubscriptionStatusComponent } from './shared/components/subscription-status/subscription-status.component';
import { TransactionHistoryComponent } from './pages/subscription/components/transaction-history/transaction-history.component';
import { ManageSubscriptionsComponent } from './pages/subscription/components/manage-subscriptions/manage-subscriptions.component';
import { ManageSubscriptionPlansComponent } from './pages/subscription/components/manage-subscription-plans/manage-subscription-plans.component';
import { CreateSubscriptionPlanComponent } from './pages/subscription/components/create-subscription-plan/create-subscription-plan.component';
import { ViewSubscriptionPlanComponent } from './pages/subscription/components/view-subscription-plan/view-subscription-plan.component';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthenticated),
    },
  },
  {
    path: 'login-callback',
    component: LogInCallbackComponent,
    canActivate: [anonymousGuard],
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    canActivate: [reauthGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'gmail-signup-error',
    component: GmailSignupErrorComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'self-signup/:id',
    component: UserSelfSignupComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'connection-self-signup',
    component: ConnectionSelfSignupComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'org-signup',
    component: OrgOnboardingCompleteComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'connection-signup-requests',
    component: ConnectionSignupRequestsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'license-agreement',
    component: LicenseAgreementComponent,
    canActivate: [anonymousGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'terms-of-use',
    component: TermsOfUseComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'cdfi-outreach-demo/chatbot',
    component: OutreachComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'cdfi-outreach/:id',
    component: OutreachComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'cdfi-outreach/:id/:uniqueId',
    component: OutreachComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'link-callback/:id',
    component: OutreachLinkCallbackComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'profile',
    component: UserProfileComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'sme/:id/profile',
    component: UserProfileDetailsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'cdfi/public/profile/:connection_name',
    component: CdfiProfileDetailsComponent,
    data: { anonymous: true },
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'cdfi/profile/:connection_name',
    component: CdfiProfileDetailsComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'cdfis/public',
    component: CdfiListComponent,
    data: { anonymous: true },
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'cdfis',
    component: CdfiListComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'search',
    component: FindLeadsV2Component,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'business',
    component: BusinessProfilesComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.SME)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'matches',
    component: MatchesComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.SME)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'business/:type',
    component: ProductDetailPageComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.SME)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'leads',
    pathMatch: 'full',
    redirectTo: 'leads/my-leads',
  },
  {
    path: 'leads/my-leads',
    component: AssignedLeadsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'leads/find',
    component: FindLeadsV2Component,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'leads/ai-search',
    component: AiSearchLabComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'files',
    component: DocumentsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'announcements',
    component: AnnouncementComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'manage-connection-group',
    component: ManageConnectionGroupComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'manage-users',
    component: ManageUsersComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'manage-duplicates',
    component: ManageDuplicatesComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'deeproots/links',
    component: OutreachLinksComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'deeproots/leads-criteria',
    component: LeadsCriteriaComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'deeproots/leads-criteria/details/:id',
    component: LeadsCriteriaDetailComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'deeproots/leads-criteria/create',
    component: LeadsCriteriaDetailComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'achievements',
    component: AchievementsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'logs/cdfi-outreach',
    component: CdfiOutreachLogsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'logs/lead-status',
    component: LeadStatusLogsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  // {
  //   path: 'logs/vlf-lifecycle',
  //   component: VlfLifecycleLogsComponent,
  //   canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
  //   resolve: {
  //     layout: setLayoutResolver(PageLayout.Authorized),
  //   },
  // },
  {
    path: 'logs/deeproots',
    component: DeeprootsLogsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'logs/audit-logs',
    component: AuditLogsComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'logs/email-log',
    component: EmailLogComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'configs/cdfi-leads-quota',
    component: CdfiLeadsQuotaComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'dnd-list',
    component: DndListComponent,
    canActivate: [authGuard, siteTypeGuard(UserAccountType.CDFI)],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'notifications',
    component: NotificationsComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'flags',
    component: UserFlagsComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'manage-flags',
    component: UserFlagsComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'flag-email',
    component: FlagEmailComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.UnAuthorized),
    },
  },
  {
    path: 'subscription',
    component: SubscriptionStatusComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'transactions',
    component: TransactionHistoryComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'manage-subscriptions',
    component: ManageSubscriptionsComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'configs/manage-subscription-plans',
    component: ManageSubscriptionPlansComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'configs/manage-subscription-plans/create',
    component: CreateSubscriptionPlanComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'configs/manage-subscription-plans/view/:plan_name',
    component: ViewSubscriptionPlanComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'configs/manage-subscription-plans/edit/:plan_name',
    component: CreateSubscriptionPlanComponent,
    canActivate: [authGuard],
    resolve: {
      layout: setLayoutResolver(PageLayout.Authorized),
    },
  },
  {
    path: 'error',
    component: ErrorComponent,
    resolve: {
      layout: setLayoutResolver(PageLayout.Error),
    },
  },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: '**', redirectTo: '/error?code=404' },
];
